import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ApiAgent } from '../../lib/api'
import { Badge, Button, Spinner, EmptyState, Panel, Table, type Column } from '../../components/ui'
import { runAgentNow } from '../../lib/cronsApi'
import { useAgentsList } from './useAgents'
import { useActiveWorkspace } from '../../lib/workspace'

export function AgentsTab({ active }: { active: boolean }) {
  const navigate = useNavigate()
  const { activeWorkspaceKey } = useActiveWorkspace()
  const { data, isLoading, isError } = useAgentsList(active)
  const [busyName, setBusyName] = useState<string | null>(null)

  const onRunNow = async (a: ApiAgent) => {
    setBusyName(`${a.workspace_key ?? ''}:${a.name}`)
    try {
      await runAgentNow(a.name, a.workspace_key || activeWorkspaceKey)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Run failed')
    } finally {
      setBusyName(null)
    }
  }

  if (isLoading) return <Spinner />
  if (isError)
    return (
      <EmptyState
        title="Agent definitions unavailable"
        hint="The agents repository isn't configured yet, or couldn't be reached."
      />
    )

  const agents = data ?? []
  if (!activeWorkspaceKey) return <EmptyState title="No active workspace." />
  if (agents.length === 0) return <EmptyState title="No agents defined." />
  const showWorkspace = agents.some((a) => a.workspace_key)

  const cols: Column<ApiAgent>[] = [
    { key: 'name', header: 'Name', render: (a) => <span className="font-medium text-gray-800">{a.name}</span> },
    ...(showWorkspace ? [{ key: 'workspace', header: 'Workspace', render: (a: ApiAgent) => <span className="text-xs text-gray-500">{a.workspace_name || a.workspace_key}</span> } satisfies Column<ApiAgent>] : []),
    { key: 'mode', header: 'Run mode', render: (a) => <Badge tone="indigo">{a.run_mode}</Badge> },
    { key: 'model', header: 'Model', render: (a) => <span className="font-mono text-xs">{a.model}</span> },
    { key: 'tools', header: 'Tools', align: 'right', render: (a) => a.tool_allowlist.length },
    { key: 'skills', header: 'Skills', align: 'right', render: (a) => a.skill_allowlist.length },
    { key: 'user-skills', header: 'User skills', render: (a) => <Badge tone={a.userskills ? 'green' : 'neutral'}>{a.userskills ? 'Enabled' : 'Disabled'}</Badge> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (a) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <Button variant="primary" disabled={busyName === `${a.workspace_key ?? ''}:${a.name}`} onClick={() => onRunNow(a)}>
            Run now
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-700">
        Agent definitions are pulled live from the configured workspace repository. This lets other agents
        iterate over the agents and their inventory in real time.
      </div>
      <Panel title={`Agents — ${agents.length}`}>
        <Table
          columns={cols}
          rows={agents}
          empty="No agents defined."
          onRowClick={(a) => navigate({ to: '/agents/$agentName', params: { agentName: a.name } })}
        />
      </Panel>
    </div>
  )
}
